// chrome.ts — the WI DOM UI DESIGN SYSTEM. ONE place for the shared look of the
// HTML/DOM UI shell: design TOKENS (colours, shadows, borders, fonts) + the el()
// helper + the shared design-STAGE CHROME PRIMITIVES, re-exporting the font layer so
// a view has a single design-system import.
//
// Every DOM view (client/src/dom/**) imports its colours/fonts/el from here instead
// of redefining them — so a styling change (the accent colour, a font, the body text
// shadow) is made ONCE and applies everywhere, and there is no "same look, different
// constant" drift between the shell forms and the in-game overlays.
//
// Phase 2 added the design-STAGE chrome the in-game overlay views share: formScale()
// (the magnification family), makeStage() (a relative N×s design stage), at() (place a
// child at forms.ini design coords), formTitle() (the kifntTitle look), and
// makeStageButton() (a factory for the 9-slice ButtonControl with its own cache + the
// up/down state swap + ksfxGuiButtonTap). Per-view differences (a different title font
// size/colour, the Style-B dialog CSS fallback) are PRESERVED by option, not flattened.

import { type ButtonArt, composeButtonDataUrl } from './title-art.ts';
import { ksfxGuiButtonTap, ksfxGuiScrollingListSelectItem } from '../ui/uikit.ts';
import { FONT_TITLE, FONT_BUTTON, FONT_BODY, measureTextPx } from './fonts.ts';

// the font layer (fonts.ts) is part of the design system — re-exported so views get
// fonts + colours from one module. buttonWidthU/measureTextPx size buttons to the WI
// face (+ the cap-aware pad) so labels never collide with the 9-slice frame.
export {
  FONT_TITLE,
  FONT_BUTTON,
  FONT_BODY,
  buttonWidthU,
  measureTextPx,
  BUTTON_PAD_U,
  warmWiFonts,
} from './fonts.ts';

// ── COLOUR TOKENS — the WI shell palette (index.html page bg + the cyan UI accent) ──
/** body / label text (≈ kiclrWhite). */
export const COL_FG = '#e8eef0';
/** the cyan UI accent — frames, slider track/thumb, checks, selection. */
export const COL_ACCENT = '#8fd0e0';
/** the shipped page background (index.html), behind the shell forms. */
export const COL_BG = 'rgb(23, 39, 42)';
/** kiclrBlack — the DialogForm border (the in-game confirm / message box default). */
export const COL_BORDER_BLACK = 'rgb(0, 0, 0)';
/** kiclrWhite — the kfMbWhiteBorder message-box border. */
export const COL_BORDER_WHITE = 'rgb(252, 252, 252)';
/** the translucent DialogForm body fill (ShadowHelper) over the live canvas. */
export const SHADOW_BG = 'rgba(0, 0, 0, 0.5)';

// ── TEXT SHADOW token — the drop shadow under label/body text for legibility ──
export const TEXT_SHADOW = '0 1px 2px rgba(0,0,0,0.85)';

/**
 * Create an element and assign inline styles in one call — the single DOM helper the
 * whole shell uses (was copy-pasted into ~13 views). `style` is a partial CSSStyleDeclaration.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  style: Partial<CSSStyleDeclaration>,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  Object.assign(e.style, style);
  return e;
}

/** Wire a "select" CLICK on a freshly-rendered control (a list row) with a GHOST-TAP
 *  guard. A tap that began on the PREVIOUS screen — e.g. the menu PLAY → MULTIPLAYER
 *  button — leaves the browser to hit-test its synthesized `click` AFTER the new screen
 *  has rendered, so the click lands on whatever element now sits under the finger. In the
 *  lobby the room list's 4th row sits ≈ where the MULTIPLAYER button was (y≈59), so the
 *  stray click silently selected the 4th room instead of the default first one (the
 *  "finger up event gets consumed by the lobby form" report). The signed-in auto-login
 *  makes the room list the FIRST interactive screen reached on that tap, which is why
 *  signing in is needed to see it.
 *
 *  A genuine direct tap/mouse press fires `pointerdown` ON this element BEFORE its click,
 *  and a keyboard activation fires a click with `detail === 0` (no pointer). A click that
 *  has NEITHER — no preceding pointerdown here AND a pointer `detail` — is the retargeted
 *  ghost, so it's dropped. Net-new, visual-neutral: a real first tap on the row still
 *  selects it, keyboard Enter/Space still activates, only the stray cross-screen click is
 *  swallowed. */
export function onSelectClick(target: HTMLElement, handler: () => void): void {
  let armed = false;
  target.addEventListener('pointerdown', () => {
    armed = true;
  });
  target.addEventListener('click', (e) => {
    if (!armed && (e as MouseEvent).detail !== 0) return; // a retargeted cross-screen ghost click
    armed = false;
    handler();
  });
}

// ── ACCESSIBILITY (keyboard) — net-new, visual-neutral for mouse users ────────────
// A KEYBOARD-ONLY focus ring on the shell's interactive controls (buttons, list rows,
// sliders, tab/checkbox controls). It uses :focus-VISIBLE, not :focus, so a pointer
// click NEVER shows the ring (the browser only matches :focus-visible for keyboard /
// programmatic focus) — a sighted mouse user sees the resting look unchanged. Injected
// ONCE as a global <style> (the composited 9-slice buttons are real <button>s, and the
// list rows / tab buttons are <button>/<label>, so a single global rule covers
// menu/lobby/dlc without touching any resting styles). Sized off the design-stage
// magnification is unnecessary — a fixed 2px COL_ACCENT outline reads clearly against
// the dark backdrop at every scale.
const A11Y_STYLE_ID = 'wi-a11y-focus';
/** Inject the global :focus-visible keyboard ring once (idempotent). Called by every
 *  shell view's constructor. No-op if the document already carries the rule (or if there
 *  is no document, e.g. a node harness). */
export function ensureA11yStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(A11Y_STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = A11Y_STYLE_ID;
  // the ring sits on EVERY focusable control via :focus-visible; the offset lifts it
  // off the 9-slice button art / list-row fill so it's legible. outline (not box-shadow)
  // so it never participates in layout / never clips the off-window zip start positions.
  //
  // touch-action:manipulation on the same control set kills the OS double-tap-to-zoom
  // gesture (and its 300ms tap delay) on these controls WITHOUT cancelling clicks — so
  // rapid taps on UI controls (e.g. queuing many units in the build panel) each land.
  // This is the safe counterpart to suppressOsZoom() in main.ts, which suppresses zoom
  // for the canvas/page by preventDefault()-ing fast touchends; that path would swallow
  // these controls' synthesized clicks, so it skips them and we rely on this rule instead.
  style.textContent =
    `:where(button, [role="option"], [role="slider"], [role="radio"], a, input):focus-visible{` +
    `outline:2px solid ${COL_ACCENT};outline-offset:2px;}` +
    `:where(button, [role="button"], [role="option"], [role="slider"], [role="radio"], a, input, select, textarea, label){` +
    `touch-action:manipulation;}`;
  (document.head ?? document.documentElement).appendChild(style);
}

/** True when the user has asked the OS for reduced motion (prefers-reduced-motion:
 *  reduce). Guards the form-open zip cascade (formchrome.ts) so the controls appear in
 *  place. Defensive: returns false where matchMedia is unavailable (a node harness). */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// ── DESIGN-STAGE CHROME PRIMITIVES ───────────────────────────────────────────────
// The in-game overlay views render their controls on a forms.ini.pp DESIGN STAGE: a
// fixed-magnification coordinate space (the canvas ShellForm/Form units) positioned
// over the live canvas. These were copy-pasted across ~9 views; they live here now.

// The canonical design SCREEN — the widest form footprint all forms are authored within
// (the shell forms are 240 wide; the in-game forms are ≤160). The canvas scales EVERY
// form's coordinates by ONE global, display-derived factor (PcFromFc × gcxFullScreenFormDiv10,
// game.cpp:1290) and then presents at the uniform kGameScale, so a 160-wide form and a
// 240-wide form share the SAME px-per-unit — the narrower one just centres with more backdrop.
const DESIGN_W = 240,
  DESIGN_H = 160,
  SCREEN_MARGIN = 8;

// The largest UI magnification — the former big-screen cap (≈2 chrome px per design unit
// at the 1.5 gameScale). Keeps the UI from ballooning on a huge monitor (where `fit` runs
// to 6–13); on phones/tablets `fit` is the binding limit, well under this.
const MAX_FORM_SCALE = 3;

// PORTRAIT boost. The 240×160 design is landscape-shaped, so a portrait PHONE width-binds
// `fit` to a tiny scale while the tall axis sits empty and the UI reads too small. On a
// phone-width portrait viewport we scale 1.5× past `fit` to fill that space (matching the
// landscape UI size) and reflow the wide 240u shell forms to fit — see PORTRAIT_FIT_W below.
// In-game forms (≤160u) still fit (160 × 1.5 / 248 = 0.97 < 1). Gated to phone widths so a
// narrow DESKTOP window (which can't reflow) keeps the no-overflow `fit`.
const PORTRAIT_BOOST = 1.5;
const PHONE_PORTRAIT_MAX_W = 480; // a portrait viewport narrower than this is a phone
// The design-unit band the wide (240u) shell forms reflow their VISIBLE content into when the
// portrait boost is active, so it stays on-screen: 240 / PORTRAIT_BOOST = 160 (≡ the in-game
// form footprint). Views read this via portraitFitWidth() to clamp lists/headers/button rows.
const PORTRAIT_FIT_W = DESIGN_W / PORTRAIT_BOOST; // 160

/**
 * The ONE magnification EVERY DOM form uses: the largest scale at which the canonical
 * 240×160 design screen still FITS the window (no clipping), capped at MAX_FORM_SCALE —
 * EXCEPT a phone-width portrait viewport, which is boosted PORTRAIT_BOOST× past `fit`
 * (the wide shell forms reflow to compensate; see {@link portraitFitWidth}).
 * Keyed off the FIXED design screen, NOT each form's own width — so all forms render at the
 * SAME scale (a narrower form just centres within its own stage with more backdrop).
 *
 * DECOUPLED from the MAP scale (main.ts kGameScale). The cap used to be 2×gameScale, and
 * gameScale collapsed to 1.0 (cap 2) whenever a window dimension was < 480 px — i.e. on
 * phones — so the UI rendered too small there even though the map scale (a separate factor)
 * was right. Now the UI fills the viewport up to MAX_FORM_SCALE regardless of the map scale,
 * so a phone in landscape uses ~2.3–2.5 instead of the old hard 2.0. (The in-game chrome's
 * own magnify follows this too — main.ts kUiMagnify = max(kGameScale, formScale()/2).)
 */
export function formScale(): number {
  const vw = window.innerWidth,
    vh = window.innerHeight;
  const fit = Math.min(vw / (DESIGN_W + SCREEN_MARGIN), vh / (DESIGN_H + SCREEN_MARGIN));
  const boost = vh > vw && vw < PHONE_PORTRAIT_MAX_W ? PORTRAIT_BOOST : 1;
  return Math.min(MAX_FORM_SCALE, fit * boost);
}

// ── SOFT-KEYBOARD SAFETY ─────────────────────────────────────────────────────────────────
// A soft keyboard changes the viewport HEIGHT only. Chrome for Android shrinks the LAYOUT
// viewport (so a `window` 'resize' fires); iOS Safari shrinks only the VISUAL viewport. Either
// way, a view whose resize handler REBUILDS its DOM (`replaceChildren`) destroys the focused
// `<input>` — and a browser blurs an element when it leaves the DOM, dropping the keyboard it
// had just opened. That self-sustains into "the keyboard won't stay up": tap → keyboard opens
// → viewport shrinks → rebuild → keyboard closes → viewport restores → rebuild → …
//
// What follows is what a view that HOSTS A TEXT FIELD needs to be keyboard-safe (today: the
// lobby, community and DLC shells + the chat overlay — `stageTextInput` is the only <input>
// factory in the DOM view family, so views without one have no keyboard to dismiss and need
// none of this). The fit path is all PURE STYLE WRITES — it must never rebuild the DOM.

/** The height of the VISUAL viewport — the band a soft keyboard leaves visible. Falls back to
 *  the layout viewport where `visualViewport` is absent (older browsers, the node test shim). */
function bandHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}

/** the last box written by {@link fitVisualViewport}, so the 2-3 events one keyboard-open
 *  fires collapse to a single set of style writes. Keyed weakly — a rebuilt overlay is a new
 *  element and simply misses. */
const lastFit = new WeakMap<HTMLElement, string>();

/**
 * Size a `position:fixed` box to the VISUAL viewport — the area the soft keyboard does NOT
 * cover — instead of the layout viewport, and report that area's height.
 *
 * A `position:fixed; inset:0` box keeps its FULL height when the keyboard opens on iOS, so
 * anything centred (or pinned to the bottom) ends up behind the keyboard. Pinning the box to
 * `visualViewport` height/offset shrinks it to the visible band. It matters on Android too:
 * the generated page can't scroll (`html,body{overflow:hidden}`, scripts/build-ts-client.mjs),
 * so viewport sizing is the ONLY thing that can keep a focused field on screen.
 *
 * Reflow-free: the `window.inner*` fallbacks (older browsers, the node test shim) sit behind
 * `??` and are evaluated only when `visualViewport` is absent. Keep it that way.
 */
export function fitVisualViewport(box: HTMLElement): number {
  const vv = window.visualViewport;
  const w = vv?.width ?? window.innerWidth;
  const h = vv?.height ?? window.innerHeight;
  const left = vv?.offsetLeft ?? 0;
  const top = vv?.offsetTop ?? 0;
  const key = `${left},${top},${w},${h}`;
  if (lastFit.get(box) !== key) {
    lastFit.set(box, key);
    Object.assign(box.style, {
      left: `${left}px`,
      top: `${top}px`,
      width: `${w}px`,
      height: `${h}px`,
      // the resting `inset:0` shorthand would otherwise fight the width/height above
      right: 'auto',
      bottom: 'auto',
    });
  }
  return h;
}

/**
 * Shrink an ALREADY-BUILT design stage to fit a shortened viewport band, by composing a factor
 * onto the centring transform the stage was built with.
 *
 * This is the no-rebuild equivalent of recomputing {@link formScale}: that function is a single
 * uniform factor whose HEIGHT term binds exactly when the stage overflows the band, so the
 * scale reproduces what a rebuild would have produced visually — without touching the DOM.
 *
 * It is a no-op (factor 1) on a phone in PORTRAIT, where the stage already fits above the
 * keyboard. It earns its keep in LANDSCAPE, where the stage is nearly full-bleed even without a
 * keyboard: without it, suppressing the rebuild would leave the form clipped by the root's
 * `overflow:hidden` — losing the title and the button row.
 *
 * Two things this must NOT do:
 *  - grow the stage (hence the clamp to 1), so portrait stays bit-identical to before; and
 *  - take the band WIDTH into account. Under the portrait boost the 240u stage DELIBERATELY
 *    overflows the viewport and only the {@link centeredBand}-reflowed core is visible, so a
 *    width term would silently undo the whole boost.
 *
 * The stage's own centring transform is CAPTURED on first fit and composed onto, never assumed:
 * the shell views centre with `translate(-50%,-50%)`, while {@link makeStage} lays its stage out
 * in flow with no transform at all. Overwriting blind would shift the latter off-screen, and a
 * per-view opt-in would fail silently the first time a new stage factory forgot it.
 *
 * Reads the stage's inline `style.height` (what the stage factory wrote), NOT `offsetHeight` —
 * a style read forces no layout, and works under the node test shim.
 */
export function fitStageToBand(stage: HTMLElement, bandH: number): void {
  const stageH = parseFloat(stage.style.height);
  // the same margin budget formScale() leaves around the 160u design screen
  const need = stageH * ((DESIGN_H + SCREEN_MARGIN) / DESIGN_H);
  const k = Number.isFinite(need) && need > 0 ? Math.min(1, bandH / need) : 1;
  // capture the authored transform once, before we ever overwrite it
  let base = stage.dataset.baseTransform;
  if (base === undefined) {
    base = stage.style.transform;
    stage.dataset.baseTransform = base;
  }
  // written UNCONDITIONALLY (scale(1) included) so the transform string is never ambiguous
  stage.style.transform = base === '' ? `scale(${k})` : `${base} scale(${k})`;
}

/** The viewport binding a keyboard-safe view holds for its lifetime. */
export interface ViewportFit {
  /** Re-fit the CURRENT DOM to the visible band. No rebuild, so a focused field keeps its
   *  focus and the keyboard stays up. Call at the end of every render; safe any time. */
  fit(): void;
  /** Drop the listeners. Call from the view's `destroy()`. */
  detach(): void;
}

/**
 * Make a view keyboard-safe: rebuild ONLY on a WIDTH change (a real orientation / window
 * resize), and treat every height-only change — plus every `visualViewport` change — as the
 * soft keyboard, which only ever re-fits.
 *
 * Suppressing the height-only rebuild is not a trade-off, it is strictly better: {@link
 * formScale} is width-bound on a phone in portrait, so the rebuild produced a pixel-identical
 * DOM and did nothing but destroy focus. (On a short viewport it was actively harmful — the
 * portrait boost could flip off mid-typing and reflow the form.)
 *
 * The suppression is only safe because it is ASYMMETRIC. A keyboard opening SHRINKS the
 * viewport, so only a height DECREASE takes the no-rebuild path; a height returning to (or
 * exceeding) the tallest height seen at this width is the keyboard closing, or a genuine window
 * grow, and rebuilds. Without that, a stage rebuilt while the keyboard was up — or a window
 * dragged shorter then taller — would be stuck at the smaller scale forever, because
 * {@link fitStageToBand} is clamped to shrink-only and nothing else recomputes {@link formScale}.
 *
 * @param root  the view's `position:fixed` root, narrowed to the visible band by `fit()`.
 * @param o.rebuild  re-render at the new size. Must end by calling `fit()` — a fresh DOM
 *                   carries none of the previous fit.
 * @param o.stage    the design stage to rescale, if the view has one. Defaults to the root's
 *                   first element child, which is what `replaceChildren(screen)` leaves.
 * @param o.alsoFit  an extra `position:fixed` box to narrow to the band (a modal overlay that
 *                   deliberately escapes the root's `overflow:hidden`).
 * @param o.skip     true while the view is hidden (a match is running, another overlay is up),
 *                   so neither `fit()` nor `rebuild()` touches an unmounted root. Left WITHOUT
 *                   updating the width watermark, so the first resize after it un-hides still
 *                   re-lays the view out.
 */
export function mountViewportFit(
  root: HTMLElement,
  o: {
    rebuild: () => void;
    stage?: () => HTMLElement | null;
    alsoFit?: () => HTMLElement | null;
    skip?: () => boolean;
  },
): ViewportFit {
  const fit = (): void => {
    if (o.skip?.() === true) return;
    const bandH = fitVisualViewport(root);
    // default: what `replaceChildren(screen)` leaves as the root's first child
    const stage =
      o.stage === undefined ? (root.firstElementChild as HTMLElement | null) : o.stage();
    if (stage !== null) fitStageToBand(stage, bandH);
    const extra = o.alsoFit?.() ?? null;
    if (extra !== null) fitVisualViewport(extra);
  };
  let lastW = window.innerWidth;
  // The watermark is the BAND (visual viewport), never window.innerHeight. A soft keyboard
  // shrinks only the VISUAL viewport on both iOS and Chrome for Android (the layout viewport
  // stays put unless the page opts into `interactive-widget=resizes-content`), so a rule keyed
  // on innerHeight reads every stray window resize while the IME is up as "restored" — and
  // rebuilding there re-derives formScale from the FULL height, producing a form too big for
  // the band that fit() then shrinks again. That alternation is visible as a twitch.
  let maxBand = bandHeight();
  // have we shrunk below the watermark? Distinguishes "the keyboard closed" (rebuild, to
  // recover the larger formScale) from "nothing changed" (a pan — just re-fit).
  let shrunk = false;
  const onViewportChange = (): void => {
    // hidden: don't lay out, and leave the watermarks stale so the first change after it
    // un-hides still rebuilds (the view's own re-entry path covers the meantime)
    if (o.skip?.() === true) return;
    const w = window.innerWidth,
      band = bandHeight();
    if (w !== lastW) {
      lastW = w;
      maxBand = band;
      shrunk = false;
      o.rebuild(); // a real size / orientation change — re-lay the form at the new scale
      return;
    }
    if (band < maxBand) {
      shrunk = true;
      fit(); // the band SHRANK — the keyboard. NEVER rebuild.
      return;
    }
    if (band > maxBand || shrunk) {
      // the keyboard closed, or the window genuinely grew: rebuild, because a shrink-clamped
      // rescale can't recover the larger formScale.
      maxBand = band;
      shrunk = false;
      o.rebuild();
      return;
    }
    fit(); // same band — a pan, or a resize that changed nothing. Cheap and memoised.
  };
  window.addEventListener('resize', onViewportChange);
  const vv = window.visualViewport;
  vv?.addEventListener('resize', onViewportChange);
  // a pan never changes the band's height, so it can't need a rebuild
  vv?.addEventListener('scroll', fit);
  return {
    fit,
    // these views are re-created per entry, so a missed removal leaks a listener bound to a
    // dead view every time
    detach: () => {
      window.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('scroll', fit);
    },
  };
}

/**
 * Run a `replaceChildren` rebuild while carrying the focused stage text field's focus and
 * caret across it, so the soft keyboard survives.
 *
 * The views re-render from scratch on a controller push, and `replaceChildren` blurs the
 * focused `<input>` by destroying it. The resize path no longer rebuilds at all (see {@link
 * mountViewportFit} — that is the real fix), and this keeps the remaining rebuilds survivable.
 *
 * Fields are keyed by the `data-field` name {@link stageTextInput} stamps, NOT by position: the
 * login and create-room forms show and hide rows, so a field's index moves and an index-keyed
 * restore would put the caret in the WRONG field. A key that isn't in the new tree simply
 * restores nothing, which is what a screen change should do.
 *
 * The restore is SYNCHRONOUS, in the same task as the rebuild — a deferred refocus is the one
 * browsers won't honour with the keyboard already on its way down. Even so, treat this as the
 * safety net: not rebuilding is what actually keeps the keyboard up.
 */
export function withFocusPreserved(root: HTMLElement, rebuild: () => void): void {
  // duck-typed, not `instanceof HTMLInputElement`: only [data-field] nodes are stage text
  // fields anyway, and the node test shim has no HTMLInputElement to be an instance of.
  const active = (typeof document === 'undefined' ? null : document.activeElement) as
    (HTMLElement & Partial<HTMLInputElement>) | null;
  const field = active?.dataset?.field;
  // `root.contains` matters: another surface's input (the chat overlay lives on document.body,
  // outside any view root) must not be dragged into this view's restore.
  const prev =
    active != null && field !== undefined && root.contains(active)
      ? { field, caret: active.selectionStart ?? 0 }
      : null;

  rebuild();

  if (prev === null) return;
  const next = root.querySelector<HTMLInputElement>(`[data-field="${prev.field}"]`);
  if (next === null) return;
  try {
    next.focus({ preventScroll: true });
    const p = Math.min(prev.caret, next.value.length);
    next.setSelectionRange(p, p);
  } catch {
    /* detached, or a shim without focus()/selection support */
  }
}

/** Focuses an input ONCE per modal open. */
export interface AutofocusOnce {
  /** Focus `input` iff `key` differs from the last one seen — i.e. this is a NEW ask, not a
   *  re-render of the one already up. Re-firing on every rebuild re-stole focus and snapped
   *  the caret to the end. Always call it on a rebuild anyway: a pending focus is re-pointed
   *  at the live node, so a re-render inside the timer's window can't strand it on a
   *  destroyed one. */
  apply(key: unknown, input: HTMLInputElement): void;
  /** The ask closed; the next {@link apply} counts as a new open. Only needed where the key
   *  can repeat (a per-ask object is already distinct by identity). */
  reset(): void;
  /** Cancel a pending focus, so it can't fire into a torn-down view. Call from `destroy()`. */
  cancel(): void;
}

/** One-shot modal autofocus — shared because the lobby's join-password ask and the DLC
 *  custom-URL ask both need exactly this, and had grown two different encodings of it. */
export function autofocusOnce(): AutofocusOnce {
  let lastKey: unknown = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const cancel = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  // the node a pending focus should land on — re-pointed by every apply(), so a rebuild that
  // lands before the timer fires moves the focus to the new input instead of a detached one
  let target: HTMLInputElement | null = null;
  return {
    apply(key, input) {
      target = input;
      if (key === lastKey) return;
      lastKey = key;
      cancel();
      // deferred so the input is mounted by the time it runs
      timer = setTimeout(() => {
        timer = null;
        try {
          target?.focus();
        } catch {
          /* detached */
        }
      }, 0);
    },
    reset() {
      lastKey = null;
      target = null;
    },
    cancel,
  };
}

/** True when the phone-portrait boost is active (so a wide shell form should reflow its
 *  content into a {@link portraitFitWidth}-unit band instead of the full 240u). */
export function isPortraitBoost(): boolean {
  const vw = window.innerWidth,
    vh = window.innerHeight;
  return vh > vw && vw < PHONE_PORTRAIT_MAX_W;
}

/** The design-unit width a wide (240u) shell form should lay its VISIBLE content out within:
 *  the boosted PORTRAIT_FIT_W (160u) under the phone-portrait boost, else the full DESIGN_W
 *  (240u). Lets a view keep its authored 240u layout on desktop/landscape and shrink the
 *  band to fit the narrow portrait width (≈the same px width as before, but 1.5× taller). */
export function portraitFitWidth(): number {
  return isPortraitBoost() ? PORTRAIT_FIT_W : DESIGN_W;
}

/** Remap a FULL-WIDTH content band of a wide (240u-stage) shell form into the centered
 *  PORTRAIT_FIT_W band under the phone-portrait boost (so it stays on-screen at the boosted
 *  scale), else return the authored {x, w} unchanged. The 240u stage is transparent and may
 *  overflow off-screen; only its visible content must stay in the viewport. Pass the authored
 *  landscape x and width (e.g. {2, 236}); in portrait you get {40, 160} (centered on x=120).
 *  For a RIGHT-anchored band, use {@link portraitFitRight}. */
export function centeredBand(x: number, w: number): { x: number; w: number } {
  if (!isPortraitBoost()) return { x, w };
  return { x: (DESIGN_W - PORTRAIT_FIT_W) / 2, w: PORTRAIT_FIT_W }; // {40, 160}
}

/** The right-edge design-unit x a RIGHT-anchored cluster should use on a wide (240u) shell
 *  form: the centered portrait band's right edge (200u) under the boost, else the authored
 *  value. Mirrors {@link centeredBand} for content anchored to the right (e.g. the DLC
 *  catalog action row). */
export function portraitFitRight(authoredRight: number): number {
  return isPortraitBoost() ? (DESIGN_W - PORTRAIT_FIT_W) / 2 + PORTRAIT_FIT_W : authoredRight; // 200
}

/** The PLAYFIELD box an in-game overlay lays out within — its own root's box. Every
 *  in-game view mounts on a `position:fixed; inset:0` root; in the shipped client that
 *  root spans the window (the fullscreen canvas), and in an embedding host (the mission
 *  editor's play-in-place pane) the root lives inside a TRANSFORMED host element that
 *  bounds it to the pane instead. Reading the root's box (not window.inner*) makes the
 *  same layout math correct in both — coordinates inside the root are playfield-relative
 *  (game px × scale needs no origin offset), and keep-on-screen clamps bound to the pane. */
export function rootBox(root: HTMLElement): { width: number; height: number } {
  // clientWidth/Height are 0 mid-mount (display:none root); fall back to the window so a
  // first paint before layout never collapses to a 0×0 clamp box.
  const w = root.clientWidth,
    h = root.clientHeight;
  return w > 0 && h > 0
    ? { width: w, height: h }
    : { width: window.innerWidth, height: window.innerHeight };
}

/** A `relative` design stage `w`×`h` design units × the scale, plus the scale used.
 *  (The centred-flex 160×160 views — game options / pause menu / objectives.) */
export function makeStage(w = 160, h = 160): { stage: HTMLElement; s: number } {
  const s = formScale();
  const stage = el('div', {
    position: 'relative',
    width: `${w * s}px`,
    height: `${h * s}px`,
  });
  return { stage, s };
}

/** Position a child at forms.ini design coords (x, y[, w, h]) within a stage, scaled
 *  by `s`. The single absolute-positioning helper every design-stage view uses. */
export function at(
  child: HTMLElement,
  s: number,
  x: number,
  y: number,
  w?: number,
  h?: number,
): HTMLElement {
  Object.assign(child.style, { position: 'absolute', left: `${x * s}px`, top: `${y * s}px` });
  if (w !== undefined) child.style.width = `${w * s}px`;
  if (h !== undefined) child.style.height = `${h * s}px`;
  return child;
}

/** Options for {@link formTitle}: a view that diverges from the default 160-form title
 *  (the dialog forms use a smaller white title on the colour bar) passes them. */
export interface FormTitleOpts {
  /** the font size in CSS px (default Math.round(7 * s) — the 160-form title size). */
  fontPx?: number;
  /** the text colour (default COL_FG; the dialog title bars pass '#fff'). */
  color?: string;
  /** the text shadow (default the 160-form '0 1px 3px rgba(0,0,0,0.8)'). */
  textShadow?: string;
}

/** The kifntTitle look: a centred, full-width, ellipsised title in the WI title face.
 *  Defaults match the 160×160 forms (7.5*s, COL_FG); `opts` preserves the dialog forms'
 *  smaller white-on-bar title. The 7.5*s default was measured against the canvas titlefont
 *  (SteelWolf) bitmap — at 7*s the web title rendered ~1px (≈1pt) shorter than the bitmap. */
export function formTitle(text: string, s: number, opts: FormTitleOpts = {}): HTMLElement {
  let fontPx = opts.fontPx ?? Math.round(7.5 * s);
  // PHONE-PORTRAIT fit: on the portrait boost (chrome scaled 1.5× past `fit`), a long title
  // — "DOWNLOAD ADD-ON MISSION PACKS", "PLAY SINGLE PLAYER MISSION" — is wider than the
  // viewport and clips (the title centres + overflows VISIBLY into the backdrop, which a
  // phone has no room for; on desktop/landscape there IS room, so it's left alone). Shrink
  // the font so the rendered text fits the viewport width. measureTextPx weighs 600 and
  // ignores the letter/word-spacing tweaks below, so pad the estimate + keep an 8px floor.
  if (isPortraitBoost()) {
    const avail = window.innerWidth - 12;
    const wText = measureTextPx(text, fontPx, FONT_TITLE) * 1.06;
    if (wText > avail) fontPx = Math.max(8, Math.floor((fontPx * avail) / wText));
  }
  const h = el('div', {
    fontFamily: FONT_TITLE,
    fontSize: `${fontPx}px`,
    fontWeight: '400',
    // ~1px of tracking (em-relative so it scales with the title) — the canvas titlefont
    // sits a touch looser than the web face at 0. The title CENTRES + overflows visibly
    // (not ellipsis): WI titles are fixed strings sized to fit, so a title a hair wider than
    // its box just extends symmetrically into the backdrop, like the canvas centres on its anchor.
    // ~1px tracking, but pull the inter-word SPACES back in (the web space glyph + the
    // tracking is wider than the canvas titlefont's space) — em-relative so both scale.
    letterSpacing: '0.05em',
    wordSpacing: '-0.32em',
    color: opts.color ?? COL_FG,
    textAlign: 'center',
    textShadow: opts.textShadow ?? '0 1px 3px rgba(0,0,0,0.8)',
    whiteSpace: 'nowrap',
    overflow: 'visible',
  });
  h.textContent = text;
  return h;
}

/** The label font size as a fraction of the button's CSS height — the ONE place
 *  the button text scale lives. 0.46 renders ~1pt smaller than the half-height
 *  (0.5) label, which ran large vs the bitmap kifntButton. NOTE: the button WIDTH
 *  is sized separately at 0.5×h by fonts.ts buttonWidthU — that is a DIFFERENT ratio
 *  (the button keeps its size; only the text drops ~1pt). Shared by makeStageButton
 *  (the in-game views) AND makeShellButton (the shell menu/lobby/dlc views). */
export const BUTTON_FONT_RATIO = 0.46;

/** A button size in CSS px. */
export interface StageButtonSize {
  w: number;
  h: number;
}

/** Options selecting the per-view button flavour (defaults = the 160-form "Style A"
 *  button: a flex 9-slice frame with a CSS pill fallback). */
export interface StageButtonOpts {
  /** the floor for the art-path font size (Math.max(fontFloor, h*0.5)). game options
   *  uses 8; pause menu / objectives use 9; the dialog buttons use 8. */
  fontFloor?: number;
  /** the CSS-fallback flavour when buttonArt is absent: 'pill' (the 160-form light
   *  accent pill — the DEFAULT) or 'dialog' (the small dark-text dialog button). */
  fallback?: 'pill' | 'dialog';
}

/** A 9-slice ButtonControl renderer bound to a view's button art + sfx, owning its own
 *  composite cache. Returns the per-view `button(label, on, size?, opts?)` the views
 *  used locally — the EXACT current behaviour: the composited 9-slice bitmap (caps +
 *  tiled middle, scaled to the box) with the pointer down/up state swap + the
 *  ksfxGuiButtonTap on press, or a CSS fallback when the art is absent.
 *
 * A view creates one in its constructor: `this.btn = makeStageButton(this.d)`.
 */
export function makeStageButton(deps: {
  buttonArt?: ButtonArt;
  playSfx?: (sfx: number) => void;
}): (
  label: string,
  onClick: () => void,
  size?: StageButtonSize,
  opts?: StageButtonOpts,
) => HTMLButtonElement {
  const cache = new Map<string, string>();
  const composite = (art: ButtonArt, wArt: number, down: boolean): string => {
    const key = `${wArt}:${down ? 1 : 0}`;
    let u = cache.get(key);
    if (u === undefined) {
      u = composeButtonDataUrl(art, wArt, down);
      cache.set(key, u);
    }
    return u;
  };

  return (label, onClick, size, opts = {}) => {
    const art = deps.buttonArt;
    const fontFloor = opts.fontFloor ?? 8;
    const tap = (): void => {
      deps.playSfx?.(ksfxGuiButtonTap);
    };

    if (art === undefined) {
      let b: HTMLButtonElement;
      if (opts.fallback === 'dialog') {
        // the small dialog button (confirm / message box / ecom / cutscene / build panel):
        // a dark-text accent pill, font sized to the box, ALWAYS sized.
        b = el('button', {
          fontSize: `${Math.max(fontFloor, Math.round((size?.h ?? 24) * BUTTON_FONT_RATIO))}px`,
          fontWeight: '600',
          fontFamily: FONT_BUTTON,
          color: '#13191b',
          background: COL_ACCENT,
          border: 'none',
          borderRadius: '3px',
          cursor: 'pointer',
        }) as HTMLButtonElement;
        if (size !== undefined) {
          b.style.width = `${size.w}px`;
          b.style.height = `${size.h}px`;
        }
      } else {
        // the 160-form pill (game options / pause menu / objectives).
        b = el('button', {
          padding: '6px 12px',
          fontSize: '13px',
          fontWeight: '600',
          letterSpacing: '0.04em',
          fontFamily: FONT_BUTTON,
          color: COL_BG,
          background: COL_ACCENT,
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          font: 'inherit',
        }) as HTMLButtonElement;
        if (size !== undefined) {
          b.style.width = `${size.w}px`;
          b.style.height = `${size.h}px`;
        }
      }
      b.textContent = label;
      b.addEventListener('pointerdown', tap);
      b.addEventListener('click', onClick);
      return b;
    }

    const wArt = size !== undefined ? Math.round((size.w / size.h) * art.midUp.cy) : 212;
    const b = el('button', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 4%',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      color: '#fff',
      fontWeight: '600',
      letterSpacing: '0.05em',
      fontFamily: FONT_BUTTON,
      whiteSpace: 'nowrap',
      textShadow: '0 1px 2px rgba(0,0,0,0.7)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '100% 100%',
    }) as HTMLButtonElement;
    if (size !== undefined) {
      b.style.width = `${size.w}px`;
      b.style.height = `${size.h}px`;
      // Label BUTTON_FONT_RATIO× the button height (was 0.5): ~1pt smaller than the bitmap
      // kifntButton looked. buttonWidthU sizes the WIDTH at 0.5×h, so the button keeps its
      // size — just smaller text (matches the menu/shell buttons).
      b.style.fontSize = `${Math.max(fontFloor, Math.round(size.h * BUTTON_FONT_RATIO))}px`;
    } else {
      b.style.minWidth = '150px';
      b.style.height = '40px';
      b.style.fontSize = '14px';
    }
    b.textContent = label;
    const setArt = (down: boolean): void => {
      b.style.backgroundImage = `url(${composite(art, wArt, down)})`;
    };
    setArt(false);
    b.addEventListener('pointerdown', () => {
      setArt(true);
      tap();
    });
    const rel = (): void => setArt(false);
    b.addEventListener('pointerup', rel);
    b.addEventListener('pointerleave', rel);
    b.addEventListener('pointercancel', rel);
    b.addEventListener('click', onClick);
    return b;
  };
}

/** The SHELL button flavour: the title-screen menu (menu-view) used a slightly larger
 *  CSS fallback than the sub-form/lobby/dlc views, so the no-art fallback is selected
 *  per-view (the art path is identical across all three). */
export type ShellButtonFallback = 'menu' | 'sub';

/** A 9-slice ButtonControl renderer for the SHELL screens (menu-view / lobby-view /
 *  dlc-view), bound to a view's button art + sfx, owning its own composite cache. This
 *  is the menu/lobby/dlc sibling of {@link makeStageButton}: the shell buttons add an
 *  `enabled` flag (disabled greys the art / dims the CSS pill + drops the handlers) that
 *  the in-game stage buttons don't need. Returns the per-view `button(label, on, enabled?,
 *  size?)` the three views used locally — the EXACT current behaviour: the composited
 *  9-slice bitmap (caps + tiled middle, scaled to the box) with the pointer down/up state
 *  swap + the ksfxGuiButtonTap on press, or a CSS fallback when the art is absent.
 *
 * A view creates one in its constructor: `this.btn = makeShellButton(this.d, 'menu')`.
 */
export function makeShellButton(
  deps: { buttonArt?: ButtonArt; playSfx?: (sfx: number) => void },
  fallback: ShellButtonFallback,
): (
  label: string,
  onClick: () => void,
  enabled?: boolean,
  size?: StageButtonSize,
) => HTMLButtonElement {
  const cache = new Map<string, string>();
  const composite = (art: ButtonArt, wArt: number, down: boolean): string => {
    const key = `${wArt}:${down ? 1 : 0}`;
    let u = cache.get(key);
    if (u === undefined) {
      u = composeButtonDataUrl(art, wArt, down);
      cache.set(key, u);
    }
    return u;
  };

  return (label, onClick, enabled = true, size) => {
    const art = deps.buttonArt;
    const tap = (): void => {
      deps.playSfx?.(ksfxGuiButtonTap);
    };

    if (art === undefined) {
      // CSS fallback (pack has no button art). The title-screen menu used a larger pill
      // than the sub-form/lobby/dlc views — preserved per `fallback`.
      const b = (
        fallback === 'menu'
          ? el('button', {
              padding: '10px 18px',
              fontSize: '15px',
              fontWeight: '600',
              letterSpacing: '0.04em',
              color: enabled ? COL_BG : 'rgba(232,238,240,0.4)',
              background: enabled ? COL_ACCENT : 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: '5px',
              cursor: enabled ? 'pointer' : 'default',
              font: 'inherit',
            })
          : el('button', {
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: '600',
              letterSpacing: '0.04em',
              fontFamily: FONT_BUTTON,
              color: enabled ? COL_BG : 'rgba(232,238,240,0.4)',
              background: enabled ? COL_ACCENT : 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: '4px',
              cursor: enabled ? 'pointer' : 'default',
              font: 'inherit',
            })
      ) as HTMLButtonElement;
      if (size !== undefined) {
        b.style.width = `${size.w}px`;
        b.style.height = `${size.h}px`;
      }
      b.textContent = label;
      b.disabled = !enabled;
      if (enabled) {
        b.addEventListener('pointerdown', tap);
        b.addEventListener('click', onClick);
      }
      return b;
    }

    // compose at the button's aspect (wArt = w/h × art height) so background-size 100% 100%
    // scales the single bitmap uniformly — no cap distortion, no seam.
    const wArt = size !== undefined ? Math.round((size.w / size.h) * art.midUp.cy) : 212;
    const b = el('button', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 4%',
      border: 'none',
      background: 'transparent',
      cursor: enabled ? 'pointer' : 'default',
      color: '#fff',
      fontWeight: '600',
      letterSpacing: '0.05em',
      fontFamily: FONT_BUTTON,
      whiteSpace: 'nowrap',
      textShadow: '0 1px 2px rgba(0,0,0,0.7)',
      opacity: enabled ? '1' : '0.5',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '100% 100%',
    }) as HTMLButtonElement;
    if (size !== undefined) {
      b.style.width = `${size.w}px`;
      b.style.height = `${size.h}px`;
      // label BUTTON_FONT_RATIO×h (width stays 0.5×h via buttonWidthU) — ~1pt smaller text, same button.
      b.style.fontSize = `${Math.max(9, Math.round(size.h * BUTTON_FONT_RATIO))}px`;
    } else {
      // no caller passes an unsized shell button today (kept for parity with makeStageButton).
      b.style.minWidth = fallback === 'menu' ? '210px' : '150px';
      b.style.height = '40px';
      b.style.fontSize = fallback === 'menu' ? '15px' : '14px';
    }
    b.textContent = label;
    b.disabled = !enabled;
    const setArt = (down: boolean): void => {
      b.style.backgroundImage = `url(${composite(art, wArt, down)})`;
    };
    setArt(false);
    if (enabled) {
      b.addEventListener('pointerdown', () => {
        setArt(true);
        tap();
      });
      const rel = (): void => setArt(false);
      b.addEventListener('pointerup', rel);
      b.addEventListener('pointerleave', rel);
      b.addEventListener('pointercancel', rel);
      b.addEventListener('click', onClick);
    }
    return b;
  };
}

// ── scrolling LIST primitive (the canvas ListControl) ─────────────────────────────
// Shared by the shell mission/load lists (menu-view) AND the in-game load/save overlay.
// A fixed-size box that scrolls when its rows overflow; placed via at() into its design
// rect. [data-list] excludes its rows from the form zip-in (the box zips as one unit).
export function stageListBox(s: number): HTMLElement {
  const box = el('div', {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    overflowY: 'auto',
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    background: 'rgba(7,22,27,0.55)',
    border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
  });
  box.dataset.list = '1';
  // a11y: the canvas ListControl → an ARIA listbox; its rows are role=option. Net-new,
  // visual-neutral (no rendered change). The caller may set aria-label for a name.
  box.setAttribute('role', 'listbox');
  return box;
}

export interface StageListItemOpts {
  /** select this row — the caller updates its selection state AND re-renders the list
   *  (so the highlight follows). The select SFX is played here before onSelect. */
  onSelect: () => void;
  /** activate (double-click / Enter equivalent) — e.g. load the slot / launch the mission. */
  onActivate: () => void;
  /** play a GUI sfx (ksfxGui*) — wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

// a selectable scrolling-list row (the canvas ListControl item): a left-aligned label;
// the SELECTED row is a SOLID steel-blue bar with dark text (kiclrButtonFillHighlight),
// click-to-select (with the select SFX) + double-click to activate. Scaled to the design
// stage. (onSelect must trigger the caller's re-render so the highlight moves.)
export function stageListItem(
  text: string,
  selected: boolean,
  s: number,
  opts: StageListItemOpts,
): HTMLElement {
  const item = el('button', {
    textAlign: 'left',
    padding: `${1.5 * s}px ${4 * s}px`,
    background: selected ? 'rgb(122, 168, 198)' : 'transparent',
    color: selected ? '#0c1a22' : COL_FG,
    border: 'none',
    cursor: 'pointer',
    font: 'inherit',
    fontFamily: FONT_BODY,
    fontSize: `${Math.round(5.5 * s)}px`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: '0 0 auto',
    textShadow: selected ? 'none' : '0 1px 2px rgba(0,0,0,0.9)',
  });
  item.textContent = text;
  // a11y: each ListControl row → role=option with aria-selected on the highlighted row
  // (the screen reader announces the selection that the steel-blue bar shows visually).
  // Net-new, visual-neutral.
  item.setAttribute('role', 'option');
  item.setAttribute('aria-selected', selected ? 'true' : 'false');
  item.addEventListener('mouseenter', () => {
    if (!selected) item.style.background = 'rgba(143,208,224,0.14)';
  });
  item.addEventListener('mouseleave', () => {
    if (!selected) item.style.background = 'transparent';
  });
  // onSelectClick (not a bare click) so a retargeted cross-screen "ghost" tap can't select
  // a row the instant this list renders under the finger (see onSelectClick).
  onSelectClick(item, () => {
    opts.playSfx?.(ksfxGuiScrollingListSelectItem);
    opts.onSelect();
  });
  item.addEventListener('dblclick', opts.onActivate);
  return item;
}

// ── shared themed TEXT INPUT (the chat / login / room-name field) ──────────────────
const STAGE_INPUT_CLASS = 'wi-stage-input';
const STAGE_INPUT_STYLE_ID = 'wi-stage-input-style';
/** Inject the ::placeholder companion to stageTextInput's numeric line-height once
 *  (idempotent). The inline line-height below centers the pre-fill VALUE (the Safari
 *  fix, PR #242), but WebKit lays the ::placeholder out with its own `normal` leading,
 *  so placeholder text ("Room name", "Player name", …) still sat low in the field.
 *  A pseudo-element can't be styled inline, so this is the one rule that needs a
 *  stylesheet: pin the placeholder to the input's own line-height. */
function ensureStageInputStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STAGE_INPUT_STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STAGE_INPUT_STYLE_ID;
  style.textContent = `.${STAGE_INPUT_CLASS}::placeholder{line-height:inherit;}`;
  (document.head ?? document.documentElement).appendChild(style);
}

/** A text input styled like the WI shell fields: a thin accent-bordered dark field that
 *  fills its box (width/height 100%, box-sizing border-box), scaled to the design stage.
 *  The single input look shared by the lobby / community / DLC forms and the chat overlay —
 *  was copy-pasted, now one helper so the field never drifts between them.
 *
 *  `field` is the stable `data-field` key {@link withFocusPreserved} restores focus by. */
export function stageTextInput(
  field: string,
  placeholder: string,
  value: string,
  s: number,
): HTMLInputElement {
  ensureStageInputStyles();
  const input = el('input', {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    font: 'inherit',
    fontFamily: FONT_BODY,
    fontSize: `${Math.round(5.5 * s)}px`,
    // An explicit numeric line-height, NOT the default `normal`: Safari/WebKit lay a pre-fill
    // value out by WIEurostile's asymmetric `normal` half-leading, so the text sat low until a
    // focus re-centered it (verified in WebKit). A numeric line-height gives symmetric leading so
    // the single line is centered in both states; 1.2 leaves descender room and fits every box
    // size this helper serves (all use a box taller than 1.2× the font).
    lineHeight: '1.2',
    padding: `0 ${3 * s}px`,
    borderRadius: `${1 * s}px`,
    border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.4)`,
    background: 'rgba(7,22,27,0.7)',
    color: COL_FG,
  }) as HTMLInputElement;
  input.setAttribute('class', STAGE_INPUT_CLASS);
  // the STABLE key withFocusPreserved uses to find this field again after a rebuild. Required,
  // so a new field can't silently opt out of keeping the soft keyboard up.
  input.dataset.field = field;
  input.placeholder = placeholder;
  input.value = value;
  return input;
}
